import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { PageHero } from "@/components/marketing/PageHero";
import { AuditForm } from "@/components/forms/AuditForm";
import { Logo } from "@/components/brand/Logo";
import { TrustRibbon } from "@/components/marketing/TrustRibbon";
import { FAQSection } from "@/components/marketing/FAQSection";
import { CONTACT_FAQS, faqJsonLd } from "@/content/faqs";
import { OG_IMAGE, ORG_ID, POSTAL_ADDRESS, SITE, abs } from "@/content/siteConfig";

type ContactSearch = {
  source?: string;
  website?: string;
  service?: string;
  score?: number;
};

export const Route = createFileRoute("/contact")({
  validateSearch: (search: Record<string, unknown>): ContactSearch => ({
    source: typeof search.source === "string" ? search.source : undefined,
    website: typeof search.website === "string" ? search.website : undefined,
    service: typeof search.service === "string" ? search.service : undefined,
    score:
      typeof search.score === "number"
        ? search.score
        : typeof search.score === "string" && search.score.trim() !== "" && !Number.isNaN(Number(search.score))
          ? Number(search.score)
          : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Contact Berk Hospitality Group: Book a 30-Minute Call" },
      {
        name: "description",
        content:
          "Request a review of your Google and Meta ad accounts: we pull the spend and conversion data and show you where the leaks are.",
      },
      { property: "og:title", content: "Contact Berk Hospitality Group: Book a 30-Minute Call" },
      {
        property: "og:description",
        content:
          "Request a review of your Google and Meta ad accounts: we pull the spend and conversion data and show you where the leaks are.",
      },
      { property: "og:url", content: abs("/contact") },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:image", content: OG_IMAGE },
      { name: "twitter:title", content: "Contact Berk Hospitality Group: Book a 30-Minute Call" },
      {
        name: "twitter:description",
        content:
          "Request a review of your Google and Meta ad accounts: we pull the spend and conversion data and show you where the leaks are.",
      },
    ],
    links: [{ rel: "canonical", href: abs("/contact") }],
    scripts: [{ type: "application/ld+json", children: faqJsonLd(CONTACT_FAQS) }],
  }),
  component: ContactPage,
});

function ContactPage() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="Contact"
        lede={
          <>
            Tell us about your operation. We&apos;ll review your current accounts, pull spend and
            conversion data, and show you where the leaks are, before you commit to anything. We work
            from Scottsdale with businesses across the Phoenix metro and nationwide.
          </>
        }
      >
        Start with an <span className="display-italic">audit.</span>
      </PageHero>

      <TrustRibbon />


      <section className="bhg-section">
        <div className="wrap">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
            <aside>
              <Logo variant="bleu" height={36} withLink={false} />
              <ul
                className="mt-12 list-none p-0"
              >
                {[
                  ["Email", SITE.email, SITE.emailHref],
                  ["Phone", SITE.phoneDisplay, SITE.phoneHref],
                  ["Location", SITE.locationDisplay, null],
                  ["Languages", SITE.languages, null],
                  [
                    "LinkedIn",
                    "/berk-hospitality-group",
                    SITE.social.linkedinCompany,
                  ],
                ].map(([lbl, val, href]) => (
                  <li
                    key={lbl as string}
                    className="grid items-baseline gap-6"
                    style={{
                      gridTemplateColumns: "120px 1fr",
                      padding: "18px 0",
                      borderBottom: "1px solid rgba(0,47,167,0.12)",
                    }}
                  >
                    <span
                      className="mono"
                      style={{
                        fontSize: 10.5,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        color: "rgba(0,47,167,0.45)",
                      }}
                    >
                      {lbl}
                    </span>
                    {href ? (
                      <a
                        href={href as string}
                        style={{ fontSize: 15, color: "#002FA7" }}
                        className="hover:text-[#8a6f3f] transition-colors"
                      >
                        {val}
                      </a>
                    ) : (
                      <span style={{ fontSize: 15, color: "#002FA7" }}>{val}</span>
                    )}
                  </li>
                ))}
              </ul>

              <div
                className="mt-10"
                style={{
                  padding: "24px",
                  background: "#f3f1e9",
                  border: "1px solid rgba(0,47,167,0.12)",
                  borderRadius: 4,
                }}
              >
                <div
                  className="mono mb-3"
                  style={{
                    fontSize: 10.5,
                    letterSpacing: "0.16em",
                    color: "rgba(0,47,167,0.55)",
                  }}
                >
                  CERTIFICATIONS
                </div>
                <ul className="m-0 list-none p-0" style={{ color: "#002FA7", fontSize: 14.5 }}>
                  <li style={{ padding: "6px 0" }}>· Certified Google Partner</li>
                  <li style={{ padding: "6px 0" }}>· Meta Business Partner</li>
                  <li style={{ padding: "6px 0" }}>· Bilingual EN / ES</li>
                </ul>
              </div>
            </aside>

            <div className="flex flex-col gap-8">
              <div
                className="grid items-center gap-6 md:grid-cols-[1fr_auto]"
                style={{
                  background: "rgba(199,154,75,0.06)",
                  border: "1px solid rgba(199,154,75,0.45)",
                  borderRadius: 6,
                  padding: "22px 24px",
                }}
              >
                <div>
                  <div
                    className="mono mb-2"
                    style={{
                      fontSize: 11,
                      letterSpacing: "0.16em",
                      color: "#C79A4B",
                    }}
                  >
                    START HERE
                  </div>
                  <h2
                    className="mb-2 text-[22px] leading-[1.15] tracking-[-0.02em]"
                    style={{ color: "#002FA7" }}
                  >
                    Get your free scan first
                  </h2>
                  <p className="max-w-xl text-[14.5px] leading-relaxed" style={{ color: "rgba(0,47,167,0.72)" }}>
                    About 15 seconds, no email. See exactly which tracking we can and cannot see on your site.
                  </p>
                </div>
                <Link
                  to="/audit"
                  className="inline-flex items-center justify-center"
                  style={{
                    gap: 8,
                    padding: "12px 20px",
                    border: "none",
                    borderRadius: 4,
                    fontFamily: '"IBM Plex Mono", "IBM Plex Mono Fallback", monospace',
                    fontSize: 11.5,
                    fontWeight: 500,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    background: "#C79A4B",
                    color: "#001A5C",
                    lineHeight: 1,
                    transition: "background .15s ease, transform .15s ease",
                    whiteSpace: "nowrap",
                  }}
                >
                  Run the free scan →
                </Link>
              </div>

              <AuditForm />
            </div>
          </div>
        </div>
      </section>

      <FAQSection items={CONTACT_FAQS} num="FAQ" title={<>Before you <span className="display-italic">reach out.</span></>} />
    </SiteShell>
  );
}
